import { Box, Tooltip } from "@mui/material";
import {
  FC,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from "react";

export interface TooltipProps {
  width: number | string;
  children: string | JSX.Element;
  isLabel?: boolean;
  isTextPre?: boolean;
  /** Show tooltip on hover even when text is not truncated (e.g. narrow table cells). */
  alwaysShow?: boolean;
  style?: CSSProperties;
  /** Optional separate content for the tooltip popup. Falls back to children if not provided. */
  tooltipContent?: string | JSX.Element;
}

export const DataTooltip: FC<TooltipProps> = ({
  width,
  children,
  isLabel,
  isTextPre,
  alwaysShow = false,
  style,
  tooltipContent,
  ...props
}) => {
  const ref = useRef<HTMLSpanElement>(null);
  /** When true, MUI should receive disableHoverListener={false} (tooltip enabled). */
  const [allowTooltip, setAllowTooltip] = useState(alwaysShow);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      if (!ref.current) return;
      if (alwaysShow) {
        setAllowTooltip(true);
        return;
      }
      setAllowTooltip(ref.current.scrollWidth > ref.current.clientWidth + 1);
    };

    measure();

    if (alwaysShow) return;

    const ro =
      typeof ResizeObserver !== "undefined"
        ? new ResizeObserver(() => {
            requestAnimationFrame(measure);
          })
        : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [children, alwaysShow]);

  return (
    <Tooltip
      enterTouchDelay={100}
      disableHoverListener={!allowTooltip}
      disableTouchListener={!allowTooltip}
      title={
        <span className={`${isTextPre ? "txt-pre-wrap" : ""}`}>
          {tooltipContent ?? children}
        </span>
      }
      PopperProps={{
        sx: {
          "&[data-popper-reference-hidden]": {
            visibility: "hidden",
            pointerEvents: "none",
          },
        },
      }}
      slotProps={{
        popper: {
          modifiers: [
            {
              name: "offset",
              options: {
                offset: [0, -14],
              },
            },
          ],
        },
      }}
    >
      <Box
        component="span"
        ref={ref}
        className={`${isLabel ? "" : "txt-ellipsis"} ${
          isTextPre ? "txt-pre" : ""
        }`}
        style={{ width, maxWidth: "100%", ...style }}
        {...props}
      >
        <span>{children}</span>
      </Box>
    </Tooltip>
  );
};
